import {
  Home,
  Building2,
  Trees,
  Caravan,
  Landmark
} from 'lucide-react';

import {
  BuildingOfficeIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

// UI-specific dropdown options)
// Dropdown choices / labels / buckets
// UI-only values for dropdowns (e.g., list of year ranges, sqft steps)

import { HomeType, SortField, MarketingStatus } from './FilterTypes';

/**
 * Marketing status options for the UI dropdown.
 * These represent user-facing choices that map to backend queries.
 */
export const marketingStatusOptions: Array<{ label: string; value: MarketingStatus }> = [
  { label: 'For sale', value: 'FOR_SALE' },
  { label: 'For rent', value: 'FOR_RENT' },
  { label: 'Sold', value: 'SOLD' },
  // Future off-market options:
  // { label: 'Rented', value: 'RENTED' },
  // { label: 'Withdrawn', value: 'WITHDRAWN' },
  // { label: 'Expired', value: 'EXPIRED' },
  // { label: 'Cancelled', value: 'CANCELLED' },
];

export const bedOptions=[ 'any', '1', '2', '3', '4', '5', '6+' ];
export const bathOptions=[ 'any', '1+', '1.5+', '2+', '2.5+', '3+', '4+' ];

export const bedValueMap: Record<string, number> = {
  'any': null,
  // 'Studio': 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6+': 100,
};

export const bathValueMap: Record<string, number | null> = {
  'any': null,
  '1+': 1,
  '1.5+': 1.5,
  '2+': 2,
  '2.5+': 2.5,
  '3+': 3,
  '4+': 100,
};

export const homeTypeOptions = [
  { label: 'House', value: 'house' as HomeType, icon: Home },
  { label: 'Townhouse', value: 'townhouse' as HomeType, icon: Building2 },
  { label: 'Condo', value: 'condo' as HomeType, icon: BuildingOfficeIcon },
  { label: 'Land', value: 'land' as HomeType, icon: Trees },
  { label: 'Multi-family', value: 'multi_family' as HomeType, icon: BuildingStorefrontIcon },
  { label: 'Mobile', value: 'mobile' as HomeType, icon: Caravan },
  { label: 'Other', value: 'other' as HomeType, icon: Landmark },
] as const;

export const statusFlagsOptions = [ 'Active', 'Pending (Under Contract)', 'Sold' ];

export const storyOptionsMin = [ 'No min', '1', '2', '3', '4', '5', '10', '15', '20', '20+' ];
export const storyOptionsMax = [ 'No max', '1', '2', '3', '4', '5', '10', '15', '20', '20+' ];
export const priceMin= '$0k';
export const priceMax= '$10M+';

export const timeOnMarketBuyOptions=[ 'No max', 'Less than 1 day', 'Less than 3 days', 'Less than 7 days', 'Less than a month 7-14 days', 'less than 60 days', 'less than 90 days', 'less than 180 days', 'less than 365 days', 'more than 365 days' ];
export const timeOnMarketRentOptions=[ 'No max', 'Less than 1 day', 'Less than 3 days', 'Less than 7 days', '7-14 days', '14-30 days', '30-60 days', '60-90 days', '90-180 days', '180-365 days', '365+ days' ];
export const timeOnMarketSoldOptions=[ 'Last 1 week', 'Last 1 month', 'Last 6 months', 'Last 1 year', 'Last 2 years', 'Last 3 years', 'Last 5 years', 'More than 5 years' ];

// Time on market value mappings (label -> days)
export const timeOnMarketValueMap: Record<string, number | null> = {
  'No max': null,
  'Less than 1 day': 1,
  'Less than 3 days': 3,
  'Less than 7 days': 7,
  'Less than a month 7-14 days': 30,
  '7-14 days': 14,
  '14-30 days': 30,
  'less than 60 days': 60,
  '30-60 days': 60,
  'less than 90 days': 90,
  '60-90 days': 90,
  'less than 180 days': 180,
  '90-180 days': 180,
  'less than 365 days': 365,
  '180-365 days': 365,
  'more than 365 days': 10000, // Use large number for "more than"
  '365+ days': 10000,
};

export const sqftOptionsMin = [ 'No min', '500', '600', '700', '800', '900', '1000', '1100', '1200', '1300', '1400', '1500', '1600', '1700', '1800', '1900', '2000', '2200', '2400', '2600', '2800', '3000', '3500', '4000', '4500', '5000', '5500', '6000', '6500', '7000', '7500', '8000', '8500', '9000', '9500', '10000' ];
export const sqftOptionsMax = [ 'No min', '500', '600', '700', '800', '900', '1000', '1100', '1200', '1300', '1400', '1500', '1600', '1700', '1800', '1900', '2000', '2200', '2400', '2600', '2800', '3000', '3500', '4000', '4500', '5000', '5500', '6000', '6500', '7000', '7500', '8000', '8500', '9000', '9500', '10000' ];
export const lotSizeOptionsMin = [ 'No min', '2,000 sqft', '4,000 sqft', '6,000 sqft', '8,000 sqft', '10,000 sqft', '.25 acres', '.5 acres', '1 acre', '2 acres', '3 acres', '4 acres', '5 acres' ];
export const lotSizeOptionsMax = [ 'No max', '2,000 sqft', '4,000 sqft', '6,000 sqft', '8,000 sqft', '10,000 sqft', '.25 acres', '.5 acres', '1 acre', '2 acres', '3 acres', '4 acres', '5 acres' ];

// need to make 10+ and create storiesMapper
export const storiesOptionsMin = [  'No min', 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20 ];
export const storiesOptionsMax = [ 'No max', 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5, 20 ];

export const yearBuiltOptionsMin = [ 'No min', '1900', '1920', '1940', '1960', '1965', '1970', '1975', '1980', '1985', '1990', '1995', '2000', '2001', '2002', '2003', '2004', '2005', '2006', '2007', '2008', '2009', '2010', '2011', '2012', '2013', '2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025' ];
export const yearBuiltOptionsMax = [ 'No max', '1900', '1920', '1940', '1960', '1965', '1970', '1975', '1980', '1985', '1990', '1995', '2000', '2001', '2002', '2003', '2004', '2005', '2006', '2007', '2008', '2009', '2010', '2011', '2012', '2013', '2014', '2015', '2016', '2017', '2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025' ];

export const propertyTaxesOptions = [ 'no max', 'no property tax', '$250/year', '1k/year', '2k/year', '3k/year', '5k/year', '10k/year', '20k/year+' ];

export const pricePerSqftOptionsMin = [ 'no min', '$50', '$100', '$150', '$200', '$250', '$300', '$400', '$500', '$600', '$800', '$1000', '$1500', 'greater than $2000' ];
export const pricePerSqftOptionsMax= [ 'no max', '$50', '$100', '$150', '$200', '$250', '$300', '$400', '$500', '$600', '$800', '$1000', '$1500', 'greater than $2000' ];

export const taxAssessedValueOptions = [ 1000, 2000, 3000, 4000, 5000 ];
export const listingTypeOptions = [ 'For Sale', 'For Rent', 'Sold' ];

// const hoaOptions = [ 'No max', 'No HOA', '$50/month', '$100/month', '$200/month', '$300/month', '$500/month', '$1000/month' ];

export const sortOptions = [
  { label: 'Best Deal', value: 'best_deal' as SortField, direction: 'desc' as const },
  { label: 'Potential Equity (high to low)', value: 'potential_equity' as SortField, direction: 'desc' as const },
  { label: 'Potential Equity (low to high)', value: 'potential_equity' as SortField, direction: 'asc' as const },
  { label: 'Newest', value: 'newest' as SortField, direction: 'asc' as const },
  { label: 'Price (low to high)', value: 'price' as SortField, direction: 'asc' as const },
  { label: 'Price (high to low)', value: 'price' as SortField, direction: 'desc' as const },
  { label: 'Square feet (low to high)', value: 'sqft' as SortField, direction: 'asc' as const },
  { label: 'Square feet (high to low)', value: 'sqft' as SortField, direction: 'desc' as const },
  { label: 'Lot size (low to high)', value: 'lot_size' as SortField, direction: 'asc' as const },
  { label: 'Lot size (high to low)', value: 'lot_size' as SortField, direction: 'desc' as const },
  { label: 'Price/sq. ft. (low to high)', value: 'price_per_sqft' as SortField, direction: 'asc' as const },
  { label: 'Price/sq. ft. (high to low)', value: 'price_per_sqft' as SortField, direction: 'desc' as const },
] as const;
